import React from "react";
import { Plus, Package } from "lucide-react";
import ProductSelector from "./ProductSelector";
import DeleteIcon from "../assets/helperIcons/DeleteIcon";

export default function InvoiceItemsTable({
  items,
  newItem,
  products,
  formatCurrency,
  handleProductSelect,
  updateItem,
  deleteItem,
  handleNewItemProductSelect,
  setNewItem,
  addItem,
}) {
  // Helper function to get product details
  const getProductDetails = (productId) => {
    return products.find((p) => p._id === productId);
  };

  return (
    <div className="rounded-lg border border-[#E6E6E6] bg-white shadow-sm">
      <table className="w-full text-sm table-fixed">
        <thead>
          <tr className="bg-[#F5F5F5] border-b border-[#E6E6E6]">
            <th className="text-left px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[30%]">Item Details</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[10%]">Qty</th>
            <th className="text-right px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[15%]">Rate</th>
            <th className="text-right px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[15%]">Discount</th>
            <th className="text-right px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[20%]">Amount</th>
            <th className="text-center px-1 py-2 font-semibold text-[#18181B] text-xs uppercase tracking-wide w-[10%]">Action</th>
          </tr>
        </thead>
        <tbody>
          {/* Existing Items */}
          {items.map((item, index) => {
            const productDetails = getProductDetails(item.productId);
            return (
              <tr
                key={`item-${index}-${item.productId || 'new'}`}
                className="border-b border-[#E6E6E6] hover:bg-[#F9F9F9] transition-colors"
              >
                {/* Item Details Column */}
                <td className="px-2 py-2" style={{ position: 'relative', zIndex: 100 - index }}>
                  <div className="space-y-2">
                    {/* Product Name with Icon */}
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {item.isPackage ? (
                          <Package className="h-5 w-5 text-purple-600 flex-shrink-0" title="Package" />
                        ) : (
                          <div className="h-5 w-5 text-[#AEADAF] flex-shrink-0">
                            <svg fill="currentColor" viewBox="0 0 20 20">
                              <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                            </svg>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <ProductSelector
                          products={products}
                          selected={item.productId}
                          disabled={item.isPackage}
                          formatCurrency={formatCurrency}
                          onChange={(productId) =>
                            handleProductSelect(index, productId)
                          }
                        />
                      </div>
                    </div>

                    {/* SKU */}
                    {productDetails && productDetails.sku && (
                      <div className="text-xs text-[#8E8C8F] ml-8">
                        SKU: <span className="font-medium text-[#18181B]">{productDetails.sku}</span>
                      </div>
                    )}

                    {/* Description */}
                    <div className="ml-8">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(index, "description", e.target.value)
                        }
                        placeholder="Add description"
                        className="w-full bg-transparent border-0 focus:outline-none text-xs text-[#8E8C8F] italic placeholder-[#C0BFC1] focus:ring-0"
                      />
                    </div>
                  </div>
                </td>

                {/* Qty */}
                <td className="px-1.5 py-2">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) =>
                      updateItem(index, "quantity", parseInt(e.target.value) || 0)
                    }
                    className="w-full text-center bg-[#F7F7F8] border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </td>

                {/* Rate */}
                <td className="px-1.5 py-2">
                  <input
                    type="number"
                    value={item.rate}
                    onChange={(e) =>
                      updateItem(index, "rate", parseFloat(e.target.value) || 0)
                    }
                    className="w-full text-right bg-[#F7F7F8] border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </td>

                {/* Discount */}
                <td className="px-1.5 py-2">
                  <input
                    type="number"
                    value={item.discount || 0}
                    onChange={(e) =>
                      updateItem(index, "discount", parseFloat(e.target.value) || 0)
                    }
                    placeholder="0"
                    className="w-full text-right bg-[#F7F7F8] border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
                  />
                </td>

                {/* Amount */}
                <td className="px-1.5 py-2 text-right">
                  <div className="font-semibold text-[#18181B]">
                    {formatCurrency(item.amount)}
                  </div>
                </td>

                {/* Actions */}
                <td className="px-1.5 py-2 text-center">
                  <button
                    onClick={() => deleteItem(index)}
                    className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-red-50 text-red-600 hover:text-red-700 transition-colors"
                  >
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
            );
          })}

          {/* Empty State */}
          {items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-12 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-12 w-12 rounded-full bg-[#F7F7F8] flex items-center justify-center text-[#AEADAF]">
                    <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0h6" />
                    </svg>
                  </div>
                  <p className="text-[#8E8C8F] text-sm">No items added yet</p>
                </div>
              </td>
            </tr>
          )}

          {/* New Item Input Row */}
          <tr className="border-t-2 border-[#D0D0D0] bg-[#F9F9F9]">
            {/* Item Details Column */}
            <td className="px-2 py-2" style={{ position: 'relative', zIndex: 9999 }}>
              <div className="space-y-2">
                {/* Product Selector */}
                <div className="flex items-start gap-2">
                  <div className="mt-1">
                    <div className="h-5 w-5 text-[#AEADAF] flex-shrink-0">
                      <svg fill="currentColor" viewBox="0 0 20 20">
                        <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                      </svg>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <ProductSelector
                      formatCurrency={formatCurrency}
                      products={products}
                      selected={newItem.productId}
                      onChange={handleNewItemProductSelect}
                    />
                  </div>
                </div>

                {/* SKU */}
                {newItem.productId && (() => {
                  const productDetails = getProductDetails(newItem.productId);
                  return productDetails && productDetails.sku ? (
                    <div className="text-xs text-[#8E8C8F] ml-8">
                      SKU: <span className="font-medium text-[#18181B]">{productDetails.sku}</span>
                    </div>
                  ) : null;
                })()}

                {/* Description */}
                <div className="ml-8">
                  <input
                    type="text"
                    placeholder="Add description"
                    value={newItem.description}
                    onChange={(e) =>
                      setNewItem({
                        ...newItem,
                        description: e.target.value,
                      })
                    }
                    className="w-full bg-transparent border-0 focus:outline-none text-xs text-[#8E8C8F] italic placeholder-[#C0BFC1] focus:ring-0"
                  />
                </div>
              </div>
            </td>

            {/* Qty */}
            <td className="px-1.5 py-2">
              <input
                type="number"
                value={newItem.quantity}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    quantity: parseInt(e.target.value) || 1,
                  })
                }
                className="w-full text-center bg-white border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </td>

            {/* Rate */}
            <td className="px-1.5 py-2">
              <input
                type="number"
                step="0.01"
                value={newItem.rate}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    rate: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full text-right bg-white border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </td>

            {/* Discount */}
            <td className="px-1.5 py-2">
              <input
                type="number"
                step="0.01"
                value={newItem.discount || 0}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    discount: parseFloat(e.target.value) || 0,
                  })
                }
                className="w-full text-right bg-white border border-[#EBEBEB] rounded px-2 py-1.5 text-sm font-medium text-[#18181B] focus:outline-none focus:ring-1 focus:ring-purple-500"
              />
            </td>

            {/* Amount */}
            <td className="px-1.5 py-2 text-right">
              <div className="font-semibold text-[#18181B]">
                {formatCurrency(newItem.quantity * newItem.rate - (newItem.discount || 0))}
              </div>
            </td>

            {/* Add Button */}
            <td className="px-1.5 py-2 text-center">
              <button
                onClick={addItem}
                className="inline-flex items-center justify-center h-8 w-8 rounded hover:bg-purple-50 text-purple-600 hover:text-purple-700 transition-colors"
                title="Add this item"
              >
                <Plus className="h-4 w-4" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}